import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../util/constants';
import { readStoredSession } from '../util/sessionStore';
import { Util } from '../util/Util';
import { shouldOpenReauthModal } from '../util/sessionExpiry';

declare module 'axios' {
	interface AxiosRequestConfig {
		skipErrorToast?: boolean;
	}
}

export const api = axios.create({
	baseURL: API_BASE_URL,
	paramsSerializer: {
		serialize: (params) => {
			const parts: string[] = [];
			Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
				if (value === null) {
					parts.push(`${encodeURIComponent(key)}=`);
					return;
				}
				if (value === undefined) return;
				if (Array.isArray(value)) {
					value.forEach((item) => {
						if (item !== undefined && item !== null) {
							parts.push(
								`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`
							);
						}
					});
					return;
				}
				parts.push(
					`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
				);
			});
			return parts.join('&');
		},
	},
});

/** Optional live token getter (Auth sets this so we prefer React session over storage). */
let liveTokenGetter: (() => string | null | undefined) | null = null;

/** Optional session hydrate when multi-tab storage has a newer token. */
let sessionHydrator: ((session: any) => void) | null = null;

export type AuthUiHandlers = {
	onSessionExpired?: (message?: string) => void;
	onSuspended?: (message: string) => void;
	onClientError?: (message: string) => void;
	onNetworkError?: () => void;
	onTimeout?: () => void;
	onServerError?: (message?: string) => void;
	/** Drop the blocking loading overlay so a failed request cannot leave it stuck. */
	onRequestFailed?: () => void;
	t?: (key: string) => string;
};

let uiHandlers: AuthUiHandlers = {};

export function setApiLiveTokenGetter(getter: (() => string | null | undefined) | null) {
	liveTokenGetter = getter;
}

export function setApiSessionHydrator(fn: ((session: any) => void) | null) {
	sessionHydrator = fn;
}

export function setApiAuthUiHandlers(handlers: AuthUiHandlers) {
	uiHandlers = handlers;
}

function resolveAccessToken(): string | null {
	const live = liveTokenGetter?.();
	if (live && Util.isAccessTokenUsable(live)) return live;
	const stored = readStoredSession()?.access_token;
	if (stored && Util.isAccessTokenUsable(stored)) return stored;
	// Prefer live even if expiry check failed (clock skew) — server is source of truth.
	if (live) return live;
	return stored || null;
}

function bearerFromConfig(config: InternalAxiosRequestConfig | undefined): string | null {
	const raw =
		(config?.headers as any)?.Authorization ||
		(config?.headers as any)?.authorization ||
		null;
	if (!raw || typeof raw !== 'string') return null;
	const m = raw.match(/^Bearer\s+(.+)$/i);
	return m ? m[1].trim() : raw.trim();
}

function setAuthHeader(config: InternalAxiosRequestConfig, token: string) {
	if (!config.headers) {
		config.headers = {} as any;
	}
	// AxiosHeaders supports set(); plain object assignment also works.
	const headers = config.headers as any;
	if (typeof headers.set === 'function') {
		headers.set('Authorization', `Bearer ${token}`);
	} else {
		headers.Authorization = `Bearer ${token}`;
	}
}

// ---------------------------------------------------------------------------
// Module-level interceptors (always on — no race with React effect mount order)
// ---------------------------------------------------------------------------

api.interceptors.request.use((config) => {
	const token = resolveAccessToken();
	if (token) {
		setAuthHeader(config, token);
	}
	const lang = localStorage.getItem('i18nextLng') || 'en';
	const headers = config.headers as any;
	if (typeof headers?.set === 'function') {
		headers.set('X-Language', lang);
		headers.set('Accept-Language', lang);
	} else if (headers) {
		headers['X-Language'] = lang;
		headers['Accept-Language'] = lang;
	}
	return config;
});

api.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const cfg = (error.config || {}) as InternalAxiosRequestConfig & {
			_retriedWithNewSession?: boolean;
			skipErrorToast?: boolean;
		};
		const reqUrl = String(
			cfg.url || (error.response as any)?.request?.responseURL || ''
		);
		const isItemImage =
			reqUrl.includes('uploads/items/') ||
			reqUrl.includes('/uploads/items/');
		const skipToast = cfg.skipErrorToast === true || isItemImage;
		const isLogoutOrLogin =
			reqUrl.includes('/logout') || reqUrl.includes('login_user');
		const isCheckSession = reqUrl.includes('check_session');

		if (!error.response) {
			if (isItemImage) return Promise.reject(error);
			uiHandlers.onRequestFailed?.();
			if (error.code === 'ECONNABORTED') {
				uiHandlers.onTimeout?.();
			} else {
				uiHandlers.onNetworkError?.();
			}
			// Resolve so callers using `await api.get` + status checks don't throw.
			return undefined as any;
		}

		const status = error.response.status;

		if (isItemImage) {
			return error.response;
		}

		if (status === 401 || status === 422) {
			const requestToken = bearerFromConfig(cfg);
			const stored = readStoredSession();
			const latest = resolveAccessToken();

			// Retry once if a newer usable token exists than the one this request used.
			if (
				!cfg._retriedWithNewSession &&
				latest &&
				requestToken &&
				latest !== requestToken &&
				Util.isAccessTokenUsable(latest)
			) {
				cfg._retriedWithNewSession = true;
				if (stored?.access_token === latest) {
					sessionHydrator?.(stored);
				}
				setAuthHeader(cfg, latest);
				return api.request(cfg);
			}

			if (
				!cfg._retriedWithNewSession &&
				stored?.access_token &&
				requestToken &&
				stored.access_token !== requestToken &&
				Util.isAccessTokenUsable(stored.access_token)
			) {
				cfg._retriedWithNewSession = true;
				sessionHydrator?.(stored);
				setAuthHeader(cfg, stored.access_token);
				return api.request(cfg);
			}

			if (
				!isLogoutOrLogin &&
				!isCheckSession &&
				!String((error.response as any)?.request?.responseURL || '').endsWith(
					'change_password'
				)
			) {
				uiHandlers.onRequestFailed?.();
				const msg = (error.response.data as any)?.message;
				if (msg && String(msg).toLowerCase().includes('suspended')) {
					uiHandlers.onSuspended?.(String(msg));
				} else if (
					shouldOpenReauthModal(resolveAccessToken(), msg, status)
				) {
					uiHandlers.onSessionExpired?.(
						msg ? String(msg) : undefined
					);
				} else if (msg && !skipToast) {
					uiHandlers.onClientError?.(String(msg));
				}
			}

			// Fulfill with response so pages can branch on status without Uncaught.
			return error.response;
		}

		if ([400, 403, 409, 404].includes(status)) {
			uiHandlers.onRequestFailed?.();
			const msg = (error.response.data as any)?.message;
			if (msg && !skipToast) {
				uiHandlers.onClientError?.(String(msg));
			}
			return error.response;
		}

		if (status === 412) {
			uiHandlers.onRequestFailed?.();
			uiHandlers.onServerError?.(
				'Server is currently under maintenance. Please try again later.'
			);
			return error.response;
		}

		if (status >= 500) {
			uiHandlers.onRequestFailed?.();
			uiHandlers.onServerError?.();
			return error.response;
		}

		return Promise.reject(error);
	}
);
